import * as React from "react"
import { cn } from "@/lib/utils"

export interface SFProIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * SF Pro symbol character to display
   */
  symbol: string
  /**
   * Size of the icon in pixels
   * @default 12
   */
  size?: number
  /**
   * Font weight
   * @default "bold"
   */
  weight?: "regular" | "medium" | "semibold" | "bold"
}

/**
 * Base SF Pro icon component
 *
 * Generic component for rendering SF Pro font symbols with consistent styling.
 * Use this directly or wrap it in semantic icon components.
 *
 * @example
 * ```tsx
 * // Direct usage
 * <SFProIcon symbol="􀌙" size={16} />
 *
 * // Wrapped in semantic component
 * export const PairingIcon = (props) => <SFProIcon symbol="􀌙" {...props} />
 * ```
 */
export const SFProIcon = React.forwardRef<HTMLSpanElement, SFProIconProps>(
  ({ symbol, size = 12, weight = "bold", className, ...props }, ref) => {
    const weightMap = {
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    }

    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center font-sf-pro shrink-0",
          weightMap[weight],
          className
        )}
        style={{ fontSize: `${size}px` }}
        aria-hidden="true"
        {...props}
      >
        {symbol}
      </span>
    )
  }
)

SFProIcon.displayName = "SFProIcon"
